// Client-side Sentry init. No-ops without VITE_SENTRY_DSN so local dev
// never crashes. PII scrubber strips emails, US phone numbers, and SSN-like
// patterns from messages, exception strings, and breadcrumb payloads.

import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const RELEASE = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev";
const MODE = import.meta.env.MODE;

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;

function scrub<T>(value: T): T {
  if (typeof value === "string") {
    return value
      .replace(EMAIL_RE, "[email]")
      .replace(SSN_RE, "[ssn]")
      .replace(PHONE_RE, "[phone]") as unknown as T;
  }
  return value;
}

let initialized = false;

export function initSentryClient() {
  if (initialized || typeof window === "undefined" || !DSN) return;
  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: MODE,
    release: RELEASE,
    tracesSampleRate: MODE === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      if (event.message) event.message = scrub(event.message);
      event.exception?.values?.forEach((ex) => {
        if (ex.value) ex.value = scrub(ex.value);
      });
      return event;
    },
    beforeBreadcrumb(crumb) {
      if (crumb.message) crumb.message = scrub(crumb.message);
      if (crumb.data && typeof crumb.data === "object") {
        for (const k of Object.keys(crumb.data)) {
          const v = (crumb.data as Record<string, unknown>)[k];
          if (typeof v === "string") (crumb.data as Record<string, unknown>)[k] = scrub(v);
        }
      }
      return crumb;
    },
  });
}

export { Sentry };